import type { ListBuilderConfiguration } from '../types';

/**
 * License Audit (Verification History) embedded list configuration.
 *
 * Drives the Audit tab on the license-detail page. Data source:
 * `GET /api/licenses/:id/verification-history` — when any of
 * page/sortFields/filters are present, the controller routes to the new
 * `findVerificationHistory` path (paginated + filtered); without them it
 * returns the legacy `{ checks, total }` shape for back-compat callers.
 *
 * Per-row "View proof" still opens a FileViewerModal in the tab
 * component — handled via the actions_dropdown column's `viewProof`
 * callback. Rows without a `proofFileId` show a `No proof file` hint
 * inline; the dropdown's option visibility is gated client-side in the
 * tab.
 *
 * Filter keys come from libs/core/src/filters/config/license-audit.ts:
 *   - auditCheckType   (PORTAL_DETAIL / API_DETAIL / BULK_IMPORT)
 *   - auditStatusNormalized (Active / Expired / etc.)
 *   - auditDisciplinary  (all / yes / no)
 *   - auditDateRange    (checked_at)
 */
export const LICENSE_AUDIT_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-license-audit-embedded',
			cacheTTL: 300000,
			name: 'Verification History',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'checked-at-column',
					fields: [
						{
							id: 'checked-at-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'checkedAt',
									objectTable: 'lm_verification_log',
								},
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'default' },
								formatOptions: { dateFormat: 'datetime-medium' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h-checked-at', text: 'Checked' }],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'checkedAt',
							objectTable: 'lm_verification_log',
						},
					],
					width: 180,
				},
				{
					id: 'status-column',
					fields: [
						{
							id: 'status-normalized',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'statusNormalizedAtCheck',
									objectTable: 'lm_verification_log',
								},
								defaultValue: 'Unknown',
							},
							displayStyle: 'badge',
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'status_map',
									statusMap: {
										Active: 'success',
										Expired: 'danger',
										Pending: 'warning',
										Revoked: 'danger',
										Suspended: 'warning',
										Cancelled: 'default',
										Other: 'default',
									},
								},
							},
						},
					],
					headers: [{ id: 'h-status', text: 'Status' }],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'statusNormalized',
							objectTable: 'lm_verification_log',
						},
					],
					width: 110,
				},
				{
					id: 'check-type-column',
					fields: [
						{
							id: 'check-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'checkType',
									objectTable: 'lm_verification_log',
								},
							},
							displayStyle: 'badge',
							styleConfig: {
								size: 'sm',
								// F13: humanize raw enum values so the rep sees
								// "Portal Detail Check" / "Bulk Import" instead
								// of PORTAL_DETAIL / BULK_IMPORT. valueMapping
								// also drives the badge variant per kind.
								// F13 + F40: humanize labels via valueMapping
								// displayValue (now an engine alias for
								// displayText post F40). Color via status_map.
								valueMapping: [
									{
										value: 'PORTAL_DETAIL',
										displayValue: 'Portal Detail Check',
									},
									{ value: 'API_DETAIL', displayValue: 'API Check' },
									{ value: 'BULK_IMPORT', displayValue: 'Bulk Import' },
									{ value: 'LOOKUP_REQUEST', displayValue: 'Lookup Request' },
									{ value: 'MANUAL', displayValue: 'Manual Check' },
								],
								colorRule: {
									type: 'status_map',
									statusMap: {
										PORTAL_DETAIL: 'info',
										API_DETAIL: 'info',
										BULK_IMPORT: 'secondary',
										LOOKUP_REQUEST: 'secondary',
										MANUAL: 'secondary',
									},
								},
							},
						},
						// F14: when a check captured a disciplinary action,
						// surface as a small warning chip appended to the
						// Type cell. Removed the standalone Disciplinary
						// column — it rendered "false" on every non-
						// disciplinary row, which is noise. The chip
						// shows ONLY when truthy via valueMapping.
						{
							id: 'disciplinary-chip',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'hasDisciplinaryAtCheck',
									objectTable: 'lm_verification_log',
								},
							},
							displayStyle: 'badge',
							styleConfig: {
								size: 'sm',
								valueMapping: [
									{ value: true, displayValue: 'Disciplinary' },
									{ value: false, displayValue: '' },
								],
								colorRule: {
									type: 'status_map',
									statusMap: {
										true: 'warning',
										false: 'default',
									},
								},
							},
						},
					],
					headers: [{ id: 'h-check-type', text: 'Type' }],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'checkType',
							objectTable: 'lm_verification_log',
						},
					],
					width: 220,
				},
				{
					id: 'expiration-column',
					fields: [
						{
							id: 'expiration-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'expirationDateAtCheck',
									objectTable: 'lm_verification_log',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { dateFormat: 'mdy' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h-expiration', text: 'Expiry Seen' }],
					sortable: false,
					width: 140,
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							// F15 (revised): "View proof" renders inline as a
							// labeled button (icon + text). Previous overflow-
							// menu placement hid the only action on the row
							// behind a three-dot button — for a tab whose
							// purpose IS inspecting per-check proof, that's
							// extra friction with no upside. F16 still
							// applies: 'open-source' is intentionally absent
							// because the stored proof_url is session-tokened
							// and goes stale; the captured screenshot is the
							// audit artifact.
							dropdownActions: [
								{
									id: 'view-proof',
									label: 'View proof',
									iconName: 'faFileMagnifyingGlass',
									callbackName: 'viewProof',
									visibilityCallback: 'canViewProof',
									visibility: 'visible',
									showLabel: true,
								},
							],
						},
						{
							// PD-14006: when a check captured no file the View-proof
							// action is hidden (canViewProof), leaving a blank cell.
							// `proofBasis` (backend-computed) fills it with a neutral
							// hint and is null when a file exists, so nothing renders
							// in that case.
							id: 'proof-basis-hint',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'proofBasis',
									objectTable: 'lm_verification_log',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					sortable: false,
					width: 140,
				},
			],
			defaultSortId: 'recent-desc',
		},
	],
	// Shared table-level sort options. These MUST live at the configuration
	// root (not inside a list) and use the TableSortOption shape
	// (`name` + `fields`) — the sort resolver reads `configuration.tableSorts`,
	// so a list-nested block or `label`/`sortFields` keys silently fail to
	// resolve `defaultSortId` and log a "did not resolve to any sort fields"
	// error, falling back to a column sort (PD-14531).
	tableSorts: [
		{
			id: 'recent-desc',
			name: 'Most recent first',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'checkedAt',
					objectTable: 'lm_verification_log',
				},
			],
		},
		{
			id: 'recent-asc',
			name: 'Oldest first',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'checkedAt',
					objectTable: 'lm_verification_log',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'auditCheckType',
			'auditStatusNormalized',
			'auditDisciplinary',
			'auditDateRange',
		],
	},
	objects: [
		{
			baseUrl: '/licenses/:id/verification-history',
			objectLabel: 'Verification Check',
			objectTable: 'lm_verification_log',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'checkedAt', fieldLabel: 'Checked At' },
				{ fieldName: 'checkType', fieldLabel: 'Check Type' },
				{ fieldName: 'statusAtCheck', fieldLabel: 'Status (raw)' },
				{
					fieldName: 'statusNormalizedAtCheck',
					fieldLabel: 'Status (normalized)',
				},
				{
					fieldName: 'expirationDateAtCheck',
					fieldLabel: 'Expiration at Check',
				},
				{
					fieldName: 'hasDisciplinaryAtCheck',
					fieldLabel: 'Disciplinary at Check',
				},
				{ fieldName: 'proofFileId', fieldLabel: 'Proof File ID' },
				{ fieldName: 'proofUrl', fieldLabel: 'Source URL' },
			],
		},
	],
};
